import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import type { CampaignWebsite } from '@/api/campaigns'
import { Badge, Card, EmptyState, LoadingBlock, type BadgeTone } from '@/design'
import { useIntegrationWebsites } from '@/features/integrations/useIntegrationWebsites'
import { ApiError } from '@/lib/api'
import { ENVIRONMENT_LABELS } from '@/lib/environment'

import { siteCredentialsBackendFor, type SiteCredentialsBackend } from './api'
import { CopyField } from './components'
import {
  DEFAULT_LABELS,
  isRevoked,
  KIND_LABELS,
  kindOf,
  STATUS_LABELS,
  type IssuedSiteCredential,
  type SiteCredential,
  type SiteCredentialKind,
} from './types'
import './websites.css'

/** Where each kind's token goes, said once beside the token itself. */
const INSTALL_NOTES: Record<SiteCredentialKind, string> = {
  ingest:
    'Paste into WordPress › Insights › Connection (server credential); the Tracking tab needs no credential.',
  read: "Set it as TMT_INSIGHTS_READ_CREDENTIAL in this website's deployment. It reads this website's growth figures for the Companion app and cannot send data.",
}

/**
 * Settings › Websites, for Owner and Administrator only (`integrations.manage`).
 *
 * Two kinds of server-side credential: the WordPress connector's (`ingest`)
 * and a read-only one for the Companion app (`read`), each row labelled by its
 * kind. The token itself is
 * never cached: it lives in the issuing card's own state for as long as the
 * card is on screen, and nowhere else.
 */
export function WebsitesPage() {
  const client = useApiClient()
  const backend = siteCredentialsBackendFor(client)
  const websites = useIntegrationWebsites()

  if (websites.isPending) return <LoadingBlock rows={4} label="Loading websites" />
  if (websites.isError) return <LoadError error={websites.error} />

  if (websites.data.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No websites yet"
        description="Connect a website in Integrations before issuing a site credential."
      />
    )
  }

  return (
    <div className="ci-websites">
      {websites.data.map((website) => (
        <WebsiteCredentialsCard
          key={website.id}
          website={website}
          backend={backend}
          clientKind={client.kind}
        />
      ))}
    </div>
  )
}

function WebsiteCredentialsCard({
  website,
  backend,
  clientKind,
}: {
  website: CampaignWebsite
  backend: SiteCredentialsBackend
  clientKind: ApiClientKind
}) {
  const queryClient = useQueryClient()
  const queryKey = ['site-credentials', clientKind, website.id] as const
  // The issued token lives here, in this card's own state, and nowhere else:
  // never in React Query's cache, never in `localStorage`. It clears the
  // moment this card unmounts, whether by dismissal or by navigating away.
  const [issued, setIssued] = useState<IssuedSiteCredential | null>(null)
  const [kind, setKind] = useState<SiteCredentialKind>('ingest')
  const [label, setLabel] = useState(DEFAULT_LABELS.ingest)

  const credentials = useQuery({ queryKey, queryFn: () => backend.list(website.id) })

  const refresh = () => queryClient.invalidateQueries({ queryKey })

  const issue = useMutation({
    mutationFn: ({ nextLabel, nextKind }: { nextLabel: string; nextKind: SiteCredentialKind }) =>
      backend.issue(website.id, nextLabel, nextKind),
    onSuccess: (result) => {
      setIssued(result)
      setKind('ingest')
      setLabel(DEFAULT_LABELS.ingest)
      revoke.reset()
      void refresh()
    },
  })

  const revoke = useMutation({
    mutationFn: (credentialId: string) => backend.revoke(website.id, credentialId),
    onSuccess: () => {
      issue.reset()
      void refresh()
    },
  })

  const busy = issue.isPending || revoke.isPending
  // Each mutation clears the other's error on its own success, so this can
  // never coalesce a stale failure from one action onto a later, unrelated one.
  const error = issue.error ?? revoke.error

  return (
    <Card
      title={website.name}
      subtitle={`${ENVIRONMENT_LABELS[website.environment]} · ${website.origin}`}
    >
      <ApiMessage error={error} />

      {issued ? (
        <div className="ci-websites__issued" role="status">
          <p className="ci-websites__issued-title">Copy it now — it will not be shown again.</p>
          <CopyField label="Site credential token" value={issued.token} />
          <p className="ci-websites__note">{INSTALL_NOTES[kindOf(issued)]}</p>
          <button
            type="button"
            className="ci-button"
            data-variant="ghost"
            onClick={() => setIssued(null)}
          >
            Done
          </button>
        </div>
      ) : null}

      {credentials.isPending ? <LoadingBlock rows={2} label={`Loading ${website.name}'s credentials`} /> : null}
      {credentials.isError ? <LoadError error={credentials.error} /> : null}

      {credentials.data ? (
        <CredentialLists
          credentials={credentials.data}
          busy={busy}
          onRevoke={(credentialId) => revoke.mutate(credentialId)}
        />
      ) : null}

      <form
        className="ci-websites__issue"
        aria-label={`Issue a site credential for ${website.name}`}
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          if (!label.trim()) return
          issue.mutate({ nextLabel: label.trim(), nextKind: kind })
        }}
      >
        <label>
          Kind
          <select
            value={kind}
            disabled={busy}
            onChange={(event) => {
              const next = event.target.value as SiteCredentialKind
              // Follow the kind with its default label, unless one was typed.
              if (label === DEFAULT_LABELS[kind]) setLabel(DEFAULT_LABELS[next])
              setKind(next)
            }}
          >
            <option value="ingest">{KIND_LABELS.ingest}</option>
            <option value="read">{KIND_LABELS.read}</option>
          </select>
        </label>
        <label>
          Label
          <input
            type="text"
            required
            value={label}
            disabled={busy}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <button type="submit" className="ci-button" data-variant="primary" disabled={busy}>
          {issue.isPending ? 'Issuing…' : 'Issue credential'}
        </button>
      </form>
    </Card>
  )
}

function CredentialLists({
  credentials,
  busy,
  onRevoke,
}: {
  credentials: readonly SiteCredential[]
  busy: boolean
  onRevoke: (credentialId: string) => void
}) {
  const active = credentials.filter((credential) => !isRevoked(credential))
  const revoked = credentials.filter(isRevoked)

  return (
    <>
      {active.length > 0 ? (
        <ul className="ci-websites__list">
          {active.map((credential) => (
            <CredentialRow
              key={credential.id}
              credential={credential}
              action={
                <button
                  type="button"
                  className="ci-button"
                  data-variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Revoke ${credential.id}? Any connector using it stops authenticating immediately.`,
                      )
                    ) {
                      onRevoke(credential.id)
                    }
                  }}
                >
                  Revoke
                </button>
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          variant="no-data"
          compact
          title="No active credentials"
          description="Issue one below to connect this website's WordPress connector."
        />
      )}

      {revoked.length > 0 ? (
        <details className="ci-websites__revoked">
          <summary>Revoked ({revoked.length})</summary>
          <ul className="ci-websites__list ci-websites__list--revoked">
            {revoked.map((credential) => (
              <CredentialRow key={credential.id} credential={credential} />
            ))}
          </ul>
        </details>
      ) : null}
    </>
  )
}

function CredentialRow({
  credential,
  action,
}: {
  credential: SiteCredential
  action?: ReactNode
}) {
  return (
    <li className="ci-websites__row">
      <span className="ci-websites__meta">
        <code>{credential.id}</code>
        <span>{credential.label}</span>
        <span className="ci-websites__kind">{KIND_LABELS[kindOf(credential)]}</span>
        <Badge tone={statusTone(credential.status)}>{STATUS_LABELS[credential.status]}</Badge>
      </span>
      <span className="ci-websites__dates">
        <small>Issued {formatDate(credential.created_at)}</small>
        {credential.rotated_at ? <small>Rotated {formatDate(credential.rotated_at)}</small> : null}
        {credential.revoked_at ? <small>Revoked {formatDate(credential.revoked_at)}</small> : null}
      </span>
      {action ? <span className="ci-websites__action">{action}</span> : null}
    </li>
  )
}

/** Whatever the API said went wrong, in its own words. */
function ApiMessage({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof ApiError ? error.message : 'Something went wrong. Try again shortly.'
  return (
    <p role="alert" className="ci-websites__error">
      {message}
    </p>
  )
}

function LoadError({ error }: { error: unknown }) {
  const refused = error instanceof ApiError && error.status === 403
  return (
    <EmptyState
      variant={refused ? 'permission' : 'outage'}
      description={
        refused
          ? 'You need Owner or Administrator access (integrations.manage) to manage site credentials — ask your tenant Owner.'
          : 'Could not load site credentials. Try again shortly.'
      }
    />
  )
}

function statusTone(status: SiteCredential['status']): BadgeTone {
  if (status === 'active') return 'positive'
  if (status === 'rotating') return 'accent'
  return 'neutral'
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString('en-GB', { dateStyle: 'medium' })
}
